import { __ResponseInfoStatus } from '../generated/graphql'

type MutationResponseFragment = {
  success: boolean
  message?: string | null
  status: __ResponseInfoStatus
  error?: string | null
}

export type SucessErrorMsg = { error?: string | None; success?: string | None }

export type Notify = {
  success?: string | undefined
  error?: string | undefined
  isLoading?: boolean
}

export function isMutationResponseFragment(val: unknown): val is MutationResponseFragment {
  return helpers.isObject(val) && 'status' in val && helpers.isString(val.status) && Object.values(__ResponseInfoStatus).includes(val.status as __ResponseInfoStatus)
}

export function isResponseInfo(val: unknown): val is { responseInfo: MutationResponseFragment } {
  return helpers.isObject(val) && 'responseInfo' in val ? isMutationResponseFragment(val.responseInfo) : false
}

const keyIsString = (o: Record<string, unknown>, k: string) => k in o && typeof o[k] === 'string'
const getValue = (o: Record<string, unknown>, k: string): string | undefined => k in o && typeof o[k] === 'string' ? o[k] : undefined

export function getGqlResponse(value: unknown | None | NotVoidPrimitives): MutationResponseFragment | null {
  if (helpers.isObject(value) && (keyIsString(value, 'success') || keyIsString(value, 'error') || keyIsString(value, 'message'))) {
    const success = getValue(value, 'success')
    const error = getValue(value, 'error')
    const message = getValue(value, 'message')
    return { status: success ? 'Success' : 'Error', success: !!success, error, message: message || error || success }
  }

  let record: unknown = helpers.isObject(value) && 'data' in value ? value.data : value
  record = helpers.isObject(record) && 'data' in record ? record.data : record

  if (!helpers.isObject(record)) return null
  if (isMutationResponseFragment(record)) return record
  if (isResponseInfo(record)) return record?.responseInfo

  record = Object.values(record).find(isResponseInfo)?.responseInfo
  if (record) return record as MutationResponseFragment

  return null
}

export function toResponse(value: unknown | None): SucessErrorMsg {
  const res = getGqlResponse(value)
  if (res?.success) return { success: res.message }
  return { error: res?.message || res?.error }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useNotify<Elem extends HTMLElement>(options?: { elem: () => HTMLElement | None }) {
  const t2 = useT2Base({
    Success: 'Sėkmė',
    Error: 'Klaida',
  })

  const elem = ref<Elem>()
  const { start } = useAsyncStopLocal()
  const timer = useTimeout()
  const notify = shallowRef<Notify>({})

  function notifySet(value: Notify, ms: number) {
    notify.value = value
    timer.set(() => (notify.value = {}), ms)
  }

  function handleError(err: unknown): void {
    if (typeof err === 'string') notifySet({ error: err }, 3000)
    else if (err instanceof Error) notifySet({ error: err.message }, 3000)
  }

  async function processPromise(cbItem: () => unknown): Promise<void> {
    let errorHandeled = false
    try {
      let resolved = false
      const { stop } = start()
      const itemP = cbItem()
      if (!helpers.isPromise(itemP)) return
      itemP?.catch?.((err) => {
        errorHandeled = true
        handleError(err)
      })?.finally?.(() => (resolved = true))

      // if ((await stop(10)) || resolved) return
      if ((await stop(10))) return
      //
      if (!resolved) notifySet({ isLoading: true }, 10000)
      if (await stop(itemP)) return
      const r = getGqlResponse(await itemP)
      notifySet(r?.success ? { success: r?.message || t2('Success') } : r?.error ? { error: r?.message || r?.error || t2('Error') } : {}, 3000)
    } catch (err) {
      if (!errorHandeled) handleError(err)
    }
  }

  return {
    notifySet,
    elem,
    elemGet: (): HTMLElement | undefined=> helpers.getHTMLElement(options?.elem()) || helpers.getHTMLElement(elem),
    notify: toRef(() => notify.value),
    error: (error: stringI18n): globalThis.ActionRes => {
      notifySet({ error }, 3000)
      return actionRes({ error })
    },
    watch: <T>(value: T): T => {
      processPromise(() => value)
      return value
    },
    watchFunc: <T>(value: () => T, res?: (val: Awaited<T>) => { success?: string | None; error?: string | None }): void => {
      processPromise(async () => {
        const val = await value()
        return res ? res(val) : val
      })
    },
  }
}
